import React from "react";
import "./ChartRenderer.css";

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#a855f7", "#6366f1"];

// ─── BAR CHART ────────────────────────────────────────────────────────────────
// Soporta múltiples datasets (barras agrupadas por label)
const BarChart = ({ data, labels, title, height = 200 }) => {
    if (!data || !labels) return null;

    const allValues = data.flatMap(ds => ds.data || []);
    const maxVal = Math.max(...allValues, 1) * 1.2;
    const isMulti = data.length > 1;

    return (
        <div className="bar-chart-container">
            {title && <h4 className="chart-title">{title}</h4>}
            <div className="bars-area" style={{ height: `${height}px` }}>
                {labels.map((label, i) => (
                    <div key={i} className="bar-wrapper" style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", height: "100%", position: "relative", gap: isMulti ? "2px" : "0" }}>
                        <div style={{ display: "flex", alignItems: "flex-end", height: "100%", gap: "3px" }}>
                            {data.map((ds, dIndex) => {
                                const val = ds.data?.[i] || 0;
                                const pct = (val / maxVal) * 100;
                                const color = ds.borderColor || ds.backgroundColor || COLORS[dIndex % COLORS.length];
                                return (
                                    <div key={dIndex} style={{ display: "flex", flexDirection: "column", alignItems: "center", height: "100%", justifyContent: "flex-end" }}>
                                        <span style={{ fontSize: "10px", fontWeight: 700, color, marginBottom: "2px" }}>{val}</span>
                                        <div style={{
                                            width: isMulti ? "18px" : "40px",
                                            height: `${pct}%`,
                                            minHeight: "2px",
                                            borderRadius: "4px 4px 0 0",
                                            background: color,
                                            transition: "height 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)"
                                        }} />
                                    </div>
                                );
                            })}
                        </div>
                        <div className="bar-label">{label}</div>
                    </div>
                ))}
            </div>
            {/* Leyenda multi-dataset */}
            {isMulti && (
                <div style={{ marginTop: "12px", display: "flex", flexWrap: "wrap", gap: "10px" }}>
                    {data.map((ds, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", color: "#94a3b8" }}>
                            <span style={{ width: "10px", height: "10px", borderRadius: "2px", backgroundColor: ds.borderColor || COLORS[i % COLORS.length] }} />
                            {ds.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── PIE CHART ────────────────────────────────────────────────────────────────
const PieChart = ({ data, title }) => {
    if (!data || !data.length) return null;
    const total = data.reduce((acc, d) => acc + d.value, 0);
    let cumulativePercent = 0;

    const getCoordinatesForPercent = (percent) => {
        const x = Math.cos(2 * Math.PI * percent);
        const y = Math.sin(2 * Math.PI * percent);
        return [x, y];
    };

    return (
        <div className="pie-chart-container">
            {title && <h4 className="chart-title">{title}</h4>}
            <div className="pie-content-wrapper">
                <svg viewBox="-1 -1 2 2" className="pie-svg" style={{ transform: "rotate(-90deg)" }}>
                    {data.map((item, i) => {
                        const [startX, startY] = getCoordinatesForPercent(cumulativePercent);
                        const percent = item.value / (total || 1);
                        cumulativePercent += percent;
                        const [endX, endY] = getCoordinatesForPercent(cumulativePercent);
                        const largeArcFlag = percent > 0.5 ? 1 : 0;
                        const pathData = [
                            `M ${startX} ${startY}`,
                            `A 1 1 0 ${largeArcFlag} 1 ${endX} ${endY}`,
                            `L 0 0 L ${startX} ${startY}`,
                        ].join(" ");
                        return <path key={i} d={pathData} fill={COLORS[i % COLORS.length]} stroke="rgba(0,0,0,0.2)" strokeWidth="0.01" />;
                    })}
                </svg>
                <div className="pie-legend">
                    {data.map((item, i) => (
                        <div key={i} className="legend-item">
                            <span className="legend-color" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                            <span className="legend-text">{item.label}: {Math.round((item.value / (total || 1)) * 100)}%</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ─── LINE CHART ───────────────────────────────────────────────────────────────
const LineChart = ({ labels, datasets, title, height = 200 }) => {
    if (!labels || !datasets) return null;

    const allValues = datasets.flatMap(ds => ds.data);
    const maxVal = Math.max(...allValues, 1) * 2.0;
    const step = 100 / (labels.length - 1 || 1);

    return (
        <div className="line-chart-container">
            {title && <h4 className="chart-title">{title}</h4>}
            <div className="line-area" style={{ height: `${height}px`, position: "relative" }}>
                <svg
                    width="100%" height="100%"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    style={{ position: "absolute", top: 0, left: 0, overflow: "visible" }}
                >
                    {datasets.map((ds, dIndex) => {
                        const points = ds.data.map((val, i) => ({
                            x: i * step,
                            y: 100 - (val / maxVal) * 100
                        }));
                        const pathData = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
                        const color = ds.borderColor || ds.backgroundColor || COLORS[dIndex % COLORS.length];
                        // FIX: soporte para líneas punteadas (ventas) vs sólidas (leads)
                        const dashArray = Array.isArray(ds.borderDash) && ds.borderDash.length
                            ? ds.borderDash.join(" ")
                            : null;

                        return (
                            <g key={dIndex}>
                                <path
                                    d={pathData}
                                    fill="none"
                                    stroke={color}
                                    strokeWidth={ds.borderWidth || 2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    {...(dashArray ? { strokeDasharray: dashArray } : {})}
                                />
                                {points.map((p, pIndex) => (
                                    <circle key={pIndex} cx={p.x} cy={p.y} r="1.5" fill={color} stroke="#1e293b" strokeWidth="0.5" />
                                ))}
                            </g>
                        );
                    })}
                </svg>
                <div className="line-labels" style={{ display: "flex", justifyContent: "space-between", position: "absolute", bottom: -25, width: "100%", padding: "0 5px" }}>
                    {labels.map((l, i) => (
                        <div key={i} className="bar-label" style={{ flex: 1, fontSize: "9px" }}>{l}</div>
                    ))}
                </div>
            </div>
            {/* Leyenda con soporte de línea punteada */}
            <div className="chart-legend" style={{ marginTop: "40px", display: "flex", flexWrap: "wrap", gap: "10px" }}>
                {datasets.map((ds, i) => {
                    const color = ds.borderColor || COLORS[i % COLORS.length];
                    const isDashed = Array.isArray(ds.borderDash) && ds.borderDash.length;
                    return (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", color: "#94a3b8" }}>
                            <svg width="16" height="10">
                                <line
                                    x1="0" y1="5" x2="16" y2="5"
                                    stroke={color}
                                    strokeWidth="2"
                                    strokeDasharray={isDashed ? ds.borderDash.join(" ") : null}
                                />
                            </svg>
                            {ds.label}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ─── CHART RENDERER ───────────────────────────────────────────────────────────
const ChartRenderer = ({ jsonStr }) => {
    try {
        const rawData = typeof jsonStr === "string" ? JSON.parse(jsonStr) : jsonStr;
        if (!rawData || (!rawData.data && !rawData.chartType && !rawData.type)) return null;

        let chartData = { ...rawData };
        const type = rawData.type || rawData.chartType || "bar";

        // FIX: resolver título desde options.plugins.title o rawData.title, NUNCA desde datasets[0].label
        const resolveTitle = (raw, fallback) =>
            raw?.options?.plugins?.title?.text || raw?.title || fallback;

        // Caso: Múltiples datasets (Formato Chart.js / Smart Chart)
        if (rawData.data && rawData.data.labels && rawData.data.datasets) {
            const labels = rawData.data.labels;
            const datasets = rawData.data.datasets;

            if (type === "line") {
                const leadsDatasets = datasets.filter(ds => ds.label?.toLowerCase().includes("lead"));
                const ventasDatasets = datasets.filter(ds => ds.label?.toLowerCase().includes("venta"));
                const isMixed = leadsDatasets.length > 0 && ventasDatasets.length > 0;

                if (isMixed) {
                    return (
                        <div className="chart-renderer-card stacked-charts">
                            <LineChart labels={labels} datasets={leadsDatasets} title="Leads por Canal" height={150} />
                            <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", margin: "25px 0 15px 0" }} />
                            <LineChart labels={labels} datasets={ventasDatasets} title="Ventas por Canal" height={185} />
                        </div>
                    );
                }

                return (
                    <div className="chart-renderer-card">
                        <LineChart labels={labels} datasets={datasets} title={resolveTitle(rawData, "Tendencias")} />
                    </div>
                );
            }

            if (type === "bar") {
                return (
                    <div className="chart-renderer-card">
                        <BarChart labels={labels} data={datasets} title={resolveTitle(rawData, "Métricas")} />
                    </div>
                );
            }

            // Pie/doughnut: colapsamos al primer dataset
            chartData.data = labels.map((l, i) => ({
                label: l,
                value: datasets[0]?.data[i] || 0
            }));
            chartData.chartType = "pie";
            chartData.title = resolveTitle(rawData, "Distribución");
        }

        // Caso: formato plano (Repli interno)
        if (Array.isArray(chartData.data)) {
            chartData.chartType = type === "pie" ? "pie" : "bar";
        }

        if (chartData.chartType === "bar") {
            const labels = chartData.labels || chartData.data.map(d => d.label);
            const values = chartData.data.map(d => d.value);
            return (
                <div className="chart-renderer-card">
                    <BarChart labels={labels} data={[{ data: values }]} title={chartData.title} />
                </div>
            );
        }

        if (chartData.chartType === "pie" || chartData.chartType === "doughnut") {
            const data = Array.isArray(chartData.data) ? chartData.data : [];
            return (
                <div className="chart-renderer-card">
                    <PieChart data={data} title={chartData.title} />
                </div>
            );
        }

        return (
            <div className="chart-renderer-card">
                <pre className="raw-json-fallback">{JSON.stringify(chartData, null, 2)}</pre>
            </div>
        );
    } catch (e) {
        console.error("Chart Render Error:", e);
        return <div className="chart-renderer-card chart-error">Error al procesar el gráfico</div>;
    }
};

export default ChartRenderer;